import { Component, computed, Input, OnDestroy, OnInit, inject } from '@angular/core';
import {
    AbstractControl,
    ControlContainer,
    ControlValueAccessor,
    NgControl,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
} from '@angular/forms';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { FormService } from '@app/services/form.service';

@Component({
    selector: 'app-hypo-control-base',
    template: '<div></div>',
})
export abstract class HypoControlsBaseComponent implements ControlValueAccessor, OnInit, OnDestroy {
    protected fb = inject(UntypedFormBuilder);
    ngControl = inject(NgControl);
    protected controlContainer = inject(ControlContainer);
    protected formService = inject(FormService);

    @Input() formControlName: string;
    @Input() tooltip: string = '';
    @Input() placeholder: string = '';
    @Input() conditionalRequired: boolean;
    @Input() hideOptionalLabel: boolean;
    @Input() testId: string = '';
    isConditionalRequired: boolean;

    public control: AbstractControl = new UntypedFormControl([]);

    public formGroup: UntypedFormGroup;
    protected destroyed$: Subject<void> = new Subject<void>();

    protected constructor() {
        if (this.ngControl != null) {
            this.ngControl.valueAccessor = this;
        }
        this.formGroup = this.fb.group({
            mainInput: [],
        });
        this.input.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
            this.onChange(value);
            this.onInputChange(value);
        });
        this.formService.resetCustomInputsSubject.pipe(takeUntil(this.destroyed$)).subscribe(() => {
            this.resetInput();
        });
        this.formService.rebuildValidatorsCustomInputsSubject
            .pipe(
                takeUntil(this.destroyed$),
                filter((a) => a === this.formControlName)
            )
            .subscribe(() => {
                this.resetValidatorsAndDisabled();
            });

        this.formService.markAsTouchedSubject.pipe(takeUntil(this.destroyed$)).subscribe(() => this.markAsTouched());
    }

    get input() {
        return this.formGroup.get('mainInput');
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onChange = (_) => {};

    onTouched = () => {};

    public ngOnInit() {
        this.isConditionalRequired = this.conditionalRequired !== undefined;
        this.input.markAsUntouched();
        if (this.controlContainer) {
            if (this.formControlName) {
                this.control = this.controlContainer.control.get(this.formControlName);
                if (!this.testId) {
                    this.testId = this.formControlName;
                }
                this.resetValidatorsAndDisabled();
            } else {
                console.warn('Missing FormControlName directive from host element of the component');
            }
        } else {
            console.warn('Cannot find parent FormGroup directive');
        }

        const old = this.control.updateValueAndValidity;
        this.control.updateValueAndValidity = (opts) => {
            old.call(this.control, opts);
            this.input.updateValueAndValidity({ emitEvent: false });
        };
    }

    ngOnDestroy(): void {
        this.destroyed$?.next();
        this.destroyed$?.complete();
    }

    writeValue(value: string) {
        this.input.setValue(value, { emitEvent: false });
    }

    registerOnChange(onChange: any) {
        this.onChange = onChange;
    }

    registerOnTouched(onTouched: any) {
        this.onTouched = onTouched;
    }

    markAsTouched() {
        if (!this.input.touched) {
            this.onTouched();
            this.formGroup.markAllAsTouched();
        }
    }

    setDisabledState(disabled: boolean): void {
        if (disabled) {
            this.input.disable({ emitEvent: false });
        } else {
            this.input.enable({ emitEvent: false });
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected onInputChange(value: any): void {}

    private resetValidatorsAndDisabled(): void {
        this.input.setValidators(this.control.validator);
        this.setDisabledState(this.control.disabled);
        this.ngControl.control.updateValueAndValidity({ emitEvent: false });
    }

    private resetInput(): void {
        setTimeout(() => {
            this.ngOnInit();
        }, 0);
    }

    showOptionalLabel = computed(() => {
        if (this.hideOptionalLabel) {
            return false;
        }
        if (this.isConditionalRequired) {
            return false;
        }
        if (this.control) {
            const validators = this.control.validator?.({} as AbstractControl);
            return !validators?.required;
        } else {
            return true;
        }
    });
}
